import { useCallback, useEffect, useRef, useState } from 'react';
import { createBlockAlignmentButton } from '@draft-js-plugins/buttons';
import buttonStyles from '../../../toolbar/button.module.less';
import formatImageLeft from '@sb1/ffe-icons/icons/open/300/md/format_image_left.svg';
import formatImageRight from '@sb1/ffe-icons/icons/open/300/md/format_image_right.svg';
import formatImageCenter from '../format_image_center.svg';
import formatImageDefault from '../format_image_default.svg';
import { Icon } from '@sb1/ffe-icons-react';

export const FormatImageDefaultButton = createBlockAlignmentButton({
    alignment: 'default',
    children: <Icon fileUrl={formatImageDefault} />,
});
export const FormatImageLeftButton = createBlockAlignmentButton({
    alignment: 'left',
    children: <Icon fileUrl={formatImageLeft} />,
});
export const FormatImageCenterButton = createBlockAlignmentButton({
    alignment: 'center',
    children: <Icon fileUrl={formatImageCenter} />,
});
export const FormatImageRightButton = createBlockAlignmentButton({
    alignment: 'right',
    children: <Icon fileUrl={formatImageRight} />,
});

function getRelativeParent(element) {
    if (!element) {
        return null;
    }
    const position = window.getComputedStyle(element).getPropertyValue('position');
    if (position !== 'static') {
        return element;
    }
    return getRelativeParent(element.parentElement);
}

export default function AlignmentTool({ store, theme }) {
    const [position, setPosition] = useState({});
    const [alignment, setAlignment] = useState(null);
    const toolbar = useRef(null);
    const ref = useRef();

    const onVisibilityChanged = useCallback((visibleBlock) => {
        ref.current = setTimeout(() => {
            let newPosition;
            const boundingRect = store.getItem('boundingRect');
            if (visibleBlock && boundingRect) {
                const relativeParent = getRelativeParent(toolbar.current.parentElement);
                const toolbarHeight = toolbar.current.clientHeight;
                const relativeRect = relativeParent
                    ? relativeParent.getBoundingClientRect()
                    : document.body.getBoundingClientRect();
                newPosition = {
                    top: boundingRect.top - relativeRect.top - toolbarHeight,
                    left: boundingRect.left - relativeRect.left + boundingRect.width / 2,
                    transform: 'translate(-50%) scale(1)',
                    transition: 'transform 0.15s cubic-bezier(.3,1.2,.2,1)',
                };
            } else {
                newPosition = { transform: 'translate(-50%) scale(0)' };
            }
            const newAlignment = store.getItem('alignment') || 'default';
            setAlignment(newAlignment);
            setPosition(newPosition);
            ref.current = undefined;
        }, 0);
    }, []);

    const onAlignmentChange = useCallback((value) => {
        if (value) {
            setAlignment(value);
        }
    }, []);

    useEffect(
        () => () => {
            //clear timeout on unmount
            if (ref.current) {
                clearTimeout(ref.current);
            }
        },
        []
    );

    useEffect(() => {
        store.subscribeToItem('visibleBlock', onVisibilityChanged);
        store.subscribeToItem('alignment', onAlignmentChange);

        return () => {
            store.unsubscribeFromItem('visibleBlock', onVisibilityChanged);
            store.unsubscribeFromItem('alignment', onAlignmentChange);
        };
    }, [onVisibilityChanged, onAlignmentChange]);

    const defaultButtons = [
        FormatImageDefaultButton,
        FormatImageLeftButton,
        FormatImageCenterButton,
        FormatImageRightButton,
    ];

    return (
        <div className={theme.alignmentTool} style={position} ref={toolbar}>
            {defaultButtons.map((Button, index) => (
                <Button
                    /* the index can be used here as the buttons list won't change */
                    key={index}
                    alignment={alignment}
                    setAlignment={store.getItem('setAlignment')}
                    theme={buttonStyles}
                />
            ))}
        </div>
    );
}
